import { createContext, useContext, useState, useEffect } from "react";

const PWAContext = createContext();

export const usePWA = () => useContext(PWAContext);

export const PWAProvider = ({ children }) => {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isEdge, setIsEdge] = useState(false);

  useEffect(() => {
    // Detect Edge browser
    const edgeDetected = navigator.userAgent.indexOf('Edg') > -1;
    setIsEdge(edgeDetected);
    
    // Skip PWA setup for Edge to prevent issues
    if (edgeDetected) {
      return;
    }
    
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(iOS);

    // Listen for beforeinstallprompt event (Android/Chrome)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setInstallPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const promptInstall = async () => {
    if (!installPrompt || isEdge) return false;
    
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setInstallPrompt(null);
      return true;
    }
    return false;
  };

  return (
    <PWAContext.Provider value={{ 
      canInstall: !isEdge && (!!installPrompt || isIOS), 
      isInstalled, 
      isIOS,
      isEdge,
      promptInstall 
    }}>
      {children}
    </PWAContext.Provider>
  );
};

export default PWAContext;
